import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * ComposureTest — Flanker task
 * Identify the center arrow direction while ignoring flankers.
 * Distraction phase: pre-mask misdirection + visual interference + 700ms window.
 * Stimulus clears after 150ms — player responds to memory trace.
 */
export default function ComposureTest({ trials, onComplete, icon, moduleNum = 5, totalModules = 6 }) {
  const canvasRef   = useRef(null);
  const overlayRef  = useRef(null);
  const wrapperRef  = useRef(null);
  const timerBarRef = useRef(null);
  const timerRafRef = useRef(null);
  const stateRef    = useRef({
    trialIndex: 0,
    onsetTime: null,
    awaitingResponse: false,
    results: [],
    timeoutId: null,
  });
  const runNextRef = useRef(null);

  const [uiState, setUiState] = useState('instructions');
  const [progress, setProgress] = useState(0);

  const RESPONSE_WINDOW  = 700;
  const STIMULUS_VISIBLE = 200;

  useEffect(() => {
    document.body.style.cursor = uiState === 'running' ? 'none' : '';
    return () => { document.body.style.cursor = ''; };
  }, [uiState]);

  const totalTrials = trials.length;

  // Timer bar — direct DOM, no React state in loop
  const stopTimerBar = useCallback(() => {
    if (timerRafRef.current) cancelAnimationFrame(timerRafRef.current);
    if (timerBarRef.current) timerBarRef.current.style.width = '0%';
  }, []);

  const startTimerBar = useCallback((duration) => {
    const bar = timerBarRef.current;
    if (!bar) return;
    bar.style.width = '100%';
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      bar.style.width = `${pct}%`;
      if (pct > 0) timerRafRef.current = requestAnimationFrame(tick);
    };
    timerRafRef.current = requestAnimationFrame(tick);
  }, []);

  // Canvas shake — direct DOM transform, no React state in loop
  const applyShake = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let start = null;
    const tick = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      if (elapsed < 500) {
        const decay = 1 - elapsed / 500;
        const offset = Math.sin((elapsed / 500) * Math.PI * 12) * 8 * decay;
        el.style.transform = `translateX(${offset}px)`;
        requestAnimationFrame(tick);
      } else {
        el.style.transform = '';
      }
    };
    requestAnimationFrame(tick);
  }, []);

  const drawBackground = useCallback((ctx, canvas) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // All arrows same color and size — center identified by position only
  const drawArrow = (ctx, x, y, direction) => {
    ctx.save();
    ctx.fillStyle = '#e2e8f0';
    ctx.font = 'bold 30px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(direction === 'left' ? '←' : '→', x, y);
    ctx.restore();
  };

  const drawFlanker = useCallback((ctx, canvas, trial) => {
    drawBackground(ctx, canvas);
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const spacing = 52;
    const flankerDir = trial.congruent
      ? trial.direction
      : (trial.direction === 'left' ? 'right' : 'left');
    drawArrow(ctx, cx - spacing * 2, cy, flankerDir);
    drawArrow(ctx, cx - spacing,     cy, flankerDir);
    drawArrow(ctx, cx,               cy, trial.direction);
    drawArrow(ctx, cx + spacing,     cy, flankerDir);
    drawArrow(ctx, cx + spacing * 2, cy, flankerDir);
  }, [drawBackground]);

  const triggerDistraction = useCallback((type) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    if (type === 'flash' || type === 'both') {
      overlay.style.background = 'rgba(255,255,255,0.65)';
      overlay.style.transition = 'none';
      overlay.style.opacity = '1';
      setTimeout(() => {
        overlay.style.transition = 'opacity 0.12s ease';
        overlay.style.opacity = '0';
      }, 100);
    }
    if (type === 'shake' || type === 'both') {
      applyShake();
    }
  }, [applyShake]);

  const runNextTrial = useCallback(() => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    stopTimerBar();

    if (s.trialIndex >= trials.length) {
      setUiState('done');
      onComplete(s.results);
      return;
    }

    const trial = trials[s.trialIndex];
    s.awaitingResponse = false;
    s.onsetTime = null;

    // Fixation cross
    drawBackground(ctx, canvas);
    ctx.fillStyle = '#4b5a72';
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('+', canvas.width / 2, canvas.height / 2);

    setProgress(Math.round((s.trialIndex / totalTrials) * 100));

    const showStimulus = () => {
      requestAnimationFrame(() => {
        drawFlanker(ctx, canvas, trial);
        s.onsetTime = performance.now();
        s.awaitingResponse = true;
        startTimerBar(RESPONSE_WINDOW);

        if (trial.distraction && trial.distractionType) {
          triggerDistraction(trial.distractionType);
        }

        // Clear stimulus after 150ms — player responds to memory trace
        setTimeout(() => {
          if (canvasRef.current) drawBackground(ctx, canvas);
        }, STIMULUS_VISIBLE);

        // Response window timeout
        s.timeoutId = setTimeout(() => {
          if (s.awaitingResponse) {
            s.awaitingResponse = false;
            stopTimerBar();
            // Red flash on timeout
            const ov = overlayRef.current;
            if (ov) {
              ov.style.background = 'rgba(239,68,68,0.35)';
              ov.style.transition = 'none';
              ov.style.opacity = '1';
              setTimeout(() => {
                ov.style.transition = 'opacity 0.15s ease';
                ov.style.opacity = '0';
              }, 120);
            }
            s.results.push({ type: 'flanker', phase: trial.phase, rt: null, correct: false });
            s.trialIndex++;
            s.timeoutId = setTimeout(() => runNextRef.current?.(), 250);
          }
        }, RESPONSE_WINDOW);
      });
    };

    s.timeoutId = setTimeout(showStimulus, trial.isi || 600);
  }, [trials, totalTrials, drawFlanker, drawBackground, triggerDistraction, startTimerBar, stopTimerBar, onComplete]);

  runNextRef.current = () => { runNextTrial(); };

  useEffect(() => {
    if (uiState !== 'running') return;

    const handleKey = (e) => {
      const s = stateRef.current;
      if (!s.awaitingResponse || s.onsetTime === null) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      e.preventDefault();
      const rt = performance.now() - s.onsetTime;
      const trial = trials[s.trialIndex];
      const pressedDir = e.key === 'ArrowLeft' ? 'left' : 'right';
      const correct = pressedDir === trial.direction;

      s.awaitingResponse = false;
      clearTimeout(s.timeoutId);
      stopTimerBar();

      s.results.push({ type: 'flanker', phase: trial.phase, rt, correct, congruent: trial.congruent });
      s.trialIndex++;
      s.timeoutId = setTimeout(() => runNextRef.current?.(), 250);
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [uiState, trials, stopTimerBar]);

  const startTest = () => {
    setUiState('running');
    setTimeout(() => runNextRef.current?.(), 500);
  };

  return (
    <div style={{ width: '100%' }}>
      {uiState === 'instructions' && (
        <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
          {icon && (
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: 'var(--accent-dim)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--accent)', margin: '0 auto 20px',
            }}>{icon}</div>
          )}
          <h2 style={{ marginBottom: 8 }}>Composure Test</h2>
          <p style={{ marginBottom: 8 }}>
            Five arrows will appear. Identify which direction the <strong>center arrow</strong> points — the middle one of five.
          </p>
          <p style={{ marginBottom: 16 }}>
            Respond quickly. The arrows won't stay on screen long.
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
            Keys: <span className="text-accent mono">← Left Arrow</span> / <span className="text-accent mono">Right Arrow →</span>
          </p>
          <button className="btn btn--primary" onClick={startTest}>Start</button>
        </div>
      )}

      {uiState === 'running' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div className="progress-bar" style={{ flex: 1 }}>
              <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{progress}%</span>
          </div>

          {/* Canvas area */}
          <div ref={wrapperRef} style={{ width: '100%', maxWidth: 600, margin: '0 auto' }}>
            {/* Canvas + flash overlay */}
            <div style={{ position: 'relative' }}>
              <canvas
                ref={canvasRef}
                width={600}
                height={380}
                style={{ width: '100%', display: 'block', background: '#0a0e1a' }}
              />
              <div
                ref={overlayRef}
                style={{
                  position: 'absolute', inset: 0, opacity: 0,
                  borderRadius: 4, pointerEvents: 'none',
                }}
              />
            </div>

            {/* Timer bar — sits flush below canvas */}
            <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
              <div
                ref={timerBarRef}
                style={{ height: '100%', width: '0%', background: 'var(--accent)', transition: 'none' }}
              />
            </div>
          </div>

          <p className="text-center text-muted" style={{ fontSize: 13, marginTop: 14 }}>
            Module {moduleNum} of {totalModules} — Composure Under Distraction
          </p>
        </div>
      )}
    </div>
  );
}
